// UI Effects Handler - Handles UI-related effects
// No network calls, just UI updates

import { DialogueEffect, DialogueEvent } from './types';
import { EffectHandler } from './EffectBus';
import { t, Lang } from '../../../../i18n';

export class UIEffectsHandler implements EffectHandler {
  private conversation: any;
  private triggerConversationUpdate: () => void;
  private scrollToBottom: () => void;
  private dispatch: (event: DialogueEvent) => void;
  private getCurrentLanguage: () => Lang;
  private onTaskCompleted?: () => void; // Optional callback when a task completes
  private pendingTaskIdForCompletion?: string; // Temporary storage for taskId when marking task as not processing

  constructor(
    conversation: any,
    triggerConversationUpdate: () => void,
    scrollToBottom: () => void,
    dispatch: (event: DialogueEvent) => void,
    getCurrentLanguage: () => Lang,
    onTaskCompleted?: () => void // Optional callback for task completion
  ) {
    this.conversation = conversation;
    this.triggerConversationUpdate = triggerConversationUpdate;
    this.scrollToBottom = scrollToBottom;
    this.dispatch = dispatch;
    this.getCurrentLanguage = getCurrentLanguage;
    this.onTaskCompleted = onTaskCompleted;
  }

  canHandle(effect: DialogueEffect): boolean {
    return [
      'ADD_MESSAGE',
      'UPDATE_STATE',
      'ASK_TASK_DESCRIPTION',
      'ASK_VARIANT',
      'ASK_AMOUNT',
      'ASK_CONFIRMATION',
      'SCROLL_TO_BOTTOM',
      'SHOW_PROCESSING',
      'HIDE_PROCESSING',
      'EMIT_PARENT_TRIGGER',
      'ADD_TASKS_TO_CONVERSATION',
      'DUPLICATE_TASK_FOR_OPTIONS',
      'MARK_TASK_COMPLETED',
      'MARK_TASK_ABORTED',
      'UPDATE_TASK_OBJECT'
    ].includes(effect.type);
  }

  /**
   * Helper function to remove parent tasks when child tasks are created
   * This ensures we NEVER have both parent and child tasks in the queue
   */
  private removeParentTasks(
    oldTaskId: string,
    newTaskId: string,
    wellDefinedTaskId: string,
    excludeTaskId?: string, // Task ID to exclude from removal (the task being updated)
    context: string = 'unknown'
  ): number {
    if (!wellDefinedTaskId || oldTaskId === newTaskId) {
      return 0;
    }

    // Check if newTaskId is a child of oldTaskId (e.g., 184210 is child of 1842)
    const isChildTask = newTaskId.startsWith(oldTaskId) && newTaskId.length > oldTaskId.length;
    
    if (!isChildTask) {
      return 0; // Not a parent-child relationship
    }

    const parentTasksToRemove = this.conversation.tasks?.filter((t: any) => 
      t.wellDefinedTaskId === wellDefinedTaskId &&
      t.taskId === oldTaskId &&
      (!excludeTaskId || t.id !== excludeTaskId) // Don't remove the task we're updating
    ) || [];

    if (parentTasksToRemove.length > 0) {
      console.log(`🗑️ [TASK_REMOVAL] [${context}] Removing ${parentTasksToRemove.length} parent task(s)`, {
        oldTaskId,
        newTaskId,
        wellDefinedTaskId,
        isChildTask,
        removedTasks: parentTasksToRemove.map((t: any) => ({
          id: t.id,
          taskId: t.taskId,
          dialogueState: t.dialogueState,
          name: t.name || t.userDescription
        })),
        totalTasksBefore: this.conversation.tasks?.length || 0
      });
      
      this.conversation.tasks = this.conversation.tasks?.filter((t: any) => 
        !(t.wellDefinedTaskId === wellDefinedTaskId && t.taskId === oldTaskId && (!excludeTaskId || t.id !== excludeTaskId))
      ) || [];
      
      console.log(`🗑️ [TASK_REMOVAL] [${context}] Removal complete`, {
        totalTasksAfter: this.conversation.tasks?.length || 0,
        removedCount: parentTasksToRemove.length
      });
    }

    return parentTasksToRemove.length;
  }

  async handle(effect: DialogueEffect): Promise<void> {
    switch (effect.type) {
      case 'ADD_MESSAGE':
        // Handle textKey for i18n messages
        if (effect.message.textKey && !effect.message.text) {
          // Use proper i18n translation
          effect.message.text = t(this.getCurrentLanguage(), effect.message.textKey as any);
        }
        this.conversation.messages.push(effect.message);
        this.triggerConversationUpdate();
        break;

      case 'UPDATE_STATE':
        // Persist the state to the conversation object
        this.conversation.updateDialogueState(effect.state);
        this.triggerConversationUpdate();
        break;

      case 'ASK_TASK_DESCRIPTION':
        // Add system message asking for task description
        this.conversation.addSystemMessage(effect.prompt, 'ask_task_description');
        this.triggerConversationUpdate();
        break;

      case 'ASK_VARIANT':
        // Add variant selection message
        const langForVariant = this.getCurrentLanguage();
        const variantMessage = {
          id: Math.random().toString(36).substr(2, 9),
          text: t(langForVariant, 'variant_selection'),
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'variantSelection',
          variantOptions: effect.options
        };
        this.conversation.messages.push(variantMessage);
        this.triggerConversationUpdate();
        break;

      case 'ASK_AMOUNT':
        this.conversation.addSystemMessage(effect.prompt, 'ask_amount');
        this.triggerConversationUpdate();
        break;

      case 'ASK_CONFIRMATION':
        this.conversation.addSystemMessage(effect.prompt, 'ask_confirmation');
        this.triggerConversationUpdate();
        break;

      case 'SCROLL_TO_BOTTOM':
        this.scrollToBottom();
        break;

      case 'SHOW_PROCESSING':
      case 'HIDE_PROCESSING':
        // Processing indicators
        break;

      case 'EMIT_PARENT_TRIGGER':
        if (effect.trigger === 'ALL_TASKS_COMPLETED') {
          this.dispatch({ type: 'TASK_COMPLETED', taskData: effect.data });
        } else if (effect.trigger === 'USER_REJECTS') {
          this.dispatch({ type: 'TASK_ABORTED', reason: 'User rejected' });
        }
        break;

      case 'ADD_TASKS_TO_CONVERSATION':
        // Add tasks to the conversation
        const existingTaskIds = new Set((this.conversation.tasks || []).map((t: any) => t.taskId).filter(Boolean));
        const tasksToAdd = (effect as any).skipIfExists
          ? effect.tasks.filter((t: any) => !existingTaskIds.has(t.taskId))
          : effect.tasks;
        
        if (tasksToAdd.length > 0) {
          console.log('➕ [TASK_CREATION] Adding tasks to conversation', {
            count: tasksToAdd.length,
            taskIds: tasksToAdd.map((t: any) => t.taskId),
            totalTasksBefore: this.conversation.tasks?.length || 0
          });

          // CRITICAL: Check if any new tasks are children of existing tasks, and remove parents
          // This handles the case where 1841 and 1842 are added, and parent 184 should be removed
          tasksToAdd.forEach((newTask: any) => {
            if (newTask.wellDefinedTaskId && newTask.taskId) {
              // Check if this new task is a child of any existing task
              const existingTasks = this.conversation.tasks || [];
              existingTasks.forEach((existingTask: any) => {
                if (existingTask.wellDefinedTaskId === newTask.wellDefinedTaskId && 
                    existingTask.taskId && 
                    newTask.taskId.startsWith(existingTask.taskId) &&
                    newTask.taskId.length > existingTask.taskId.length) {
                  // newTask is a child of existingTask - remove parent
                  this.removeParentTasks(
                    existingTask.taskId,
                    newTask.taskId,
                    newTask.wellDefinedTaskId,
                    undefined,
                    'ADD_TASKS_TO_CONVERSATION'
                  );
                }
              });
            }
          });

          // CRITICAL: Also check if multiple new tasks share the same parent
          // When adding [1841, 1842], both are children of 184, so remove 184 once
          if (tasksToAdd.length > 1) {
            const newTaskIds = new Set(tasksToAdd.map((t: any) => t.taskId).filter(Boolean));
            const wellDefinedTaskIds = new Set(tasksToAdd.map((t: any) => t.wellDefinedTaskId).filter(Boolean));
            
            // For each wellDefinedTaskId, find if all new tasks share a common parent
            wellDefinedTaskIds.forEach((wellDefinedTaskId) => {
              const tasksForThisWellDefined = tasksToAdd.filter((t: any) => t.wellDefinedTaskId === wellDefinedTaskId);
              if (tasksForThisWellDefined.length > 1) {
                // Find the shortest taskId - this is likely the parent
                const taskIds = tasksForThisWellDefined.map((t: any) => t.taskId).filter(Boolean).sort((a, b) => a.length - b.length);
                if (taskIds.length > 0) {
                  const shortestTaskId = taskIds[0];
                  // Check if all other taskIds are children of the shortest one
                  const allAreChildren = taskIds.every(taskId => 
                    taskId === shortestTaskId || 
                    (taskId.startsWith(shortestTaskId) && taskId.length > shortestTaskId.length)
                  );
                  
                  if (allAreChildren && taskIds.length > 1) {
                    // All new tasks are children of shortestTaskId - remove parent
                    const existingParent = this.conversation.tasks?.find((t: any) => 
                      t.wellDefinedTaskId === wellDefinedTaskId && 
                      t.taskId === shortestTaskId &&
                      !newTaskIds.has(t.taskId) // Don't remove if it's one of the new tasks
                    );
                    
                    if (existingParent) {
                      this.removeParentTasks(
                        shortestTaskId,
                        taskIds[1], // Use first child as reference
                        wellDefinedTaskId,
                        undefined,
                        'ADD_TASKS_TO_CONVERSATION (multi-child)'
                      );
                    }
                  }
                }
              }
            });
          }

          this.conversation.tasks = [...(this.conversation.tasks || []), ...tasksToAdd];
          console.log('➕ [TASK_CREATION] Tasks added', {
            totalTasksAfter: this.conversation.tasks?.length || 0
          });
          this.triggerConversationUpdate();
        }
        break;

      case 'DUPLICATE_TASK_FOR_OPTIONS':
        // Duplicate a task object for each selected option (for multi-select at deeper levels)
        const taskToDuplicate = this.conversation.tasks?.find((t: any) => 
          t.id === effect.taskId || 
          (t.taskId === effect.taskId && t.wellDefinedTaskId)
        );
        
        if (!taskToDuplicate) {
          console.error('❌ [DUPLICATE_TASK_FOR_OPTIONS] Task not found:', effect.taskId);
          break;
        }
        
        console.log('➕ [TASK_CREATION] Duplicating task for options', {
          originalTaskId: taskToDuplicate.taskId,
          originalId: taskToDuplicate.id,
          selectedCount: effect.selectedOptionIds.length,
          removeOriginal: effect.removeOriginal
        });
        
        // Create duplicates for each selected option
        const duplicatedTasks = effect.selectedOptionIds.map((selectedOptionId, index) => {
          const optionName = effect.optionDescriptions[selectedOptionId] || selectedOptionId;
          
          const duplicatedTask = {
            ...taskToDuplicate,
            id: Math.random().toString(36).substr(2, 9), // New unique ID
            taskId: selectedOptionId, // Update to selected option ID
            userDescription: optionName,
            name: taskToDuplicate.name ? `${taskToDuplicate.name}; ${optionName}` : optionName,
            dialogueState: 'leaf_validation' as const,
            isProcessingDialogue: false,
            isSubmitted: false
          };
          
          console.log(`➕ [TASK_CREATION] Created duplicate ${index + 1}/${effect.selectedOptionIds.length}`, {
            originalTaskId: taskToDuplicate.taskId,
            newTaskId: selectedOptionId,
            newId: duplicatedTask.id
          });
          
          return duplicatedTask;
        });
        
        // CRITICAL: Remove parent tasks with the old taskId when child tasks are created
        const oldTaskId = taskToDuplicate.taskId;
        const wellDefinedTaskId = taskToDuplicate.wellDefinedTaskId;
        const newTaskIds = new Set(effect.selectedOptionIds);
        
        if (wellDefinedTaskId && oldTaskId && !newTaskIds.has(oldTaskId)) {
          // Remove parent tasks (but not the one we're duplicating - will be removed separately if removeOriginal is true)
          this.removeParentTasks(
            oldTaskId,
            effect.selectedOptionIds[0], // Use first new taskId to check parent-child relationship
            wellDefinedTaskId,
            taskToDuplicate.id, // Exclude the task we're duplicating
            'DUPLICATE_TASK_FOR_OPTIONS'
          );
        }
        
        // Remove original task if requested (for multi-select, not single)
        if (effect.removeOriginal) {
          const beforeCount = this.conversation.tasks?.length || 0;
          this.conversation.tasks = this.conversation.tasks?.filter((t: any) => t.id !== taskToDuplicate.id) || [];
          console.log('🗑️ [TASK_REMOVAL] Removed original task', {
            taskId: taskToDuplicate.taskId,
            id: taskToDuplicate.id,
            tasksBefore: beforeCount,
            tasksAfter: this.conversation.tasks?.length || 0
          });
        }
        
        // Add duplicated tasks
        const beforeAdd = this.conversation.tasks?.length || 0;
        this.conversation.tasks = [...(this.conversation.tasks || []), ...duplicatedTasks];
        console.log('➕ [TASK_CREATION] Duplication complete', {
          duplicatesCreated: duplicatedTasks.length,
          tasksBefore: beforeAdd,
          tasksAfter: this.conversation.tasks?.length || 0
        });
        this.triggerConversationUpdate();
        break;

      case 'MARK_TASK_COMPLETED':
        // Find the task(s) to mark as completed
        let tasksToComplete: any[] = [];
        if (effect.wellDefinedTaskId) {
          tasksToComplete = this.conversation.tasks?.filter((t: any) => t.wellDefinedTaskId === effect.wellDefinedTaskId) || [];
        } else {
          if (effect.taskId) {
            const taskById = this.conversation.tasks?.find((t: any) => t.taskId === effect.taskId);
            if (taskById) {
              tasksToComplete = [taskById];
            }
          } else if (this.pendingTaskIdForCompletion) {
            const taskById = this.conversation.tasks?.find((t: any) => t.taskId === this.pendingTaskIdForCompletion);
            if (taskById) {
              tasksToComplete = [taskById];
              this.pendingTaskIdForCompletion = undefined;
            }
          }
          
          if (tasksToComplete.length === 0) {
            const activeTasks = this.conversation.tasks?.filter((t: any) => t.isProcessingDialogue) || [];
            if (activeTasks.length > 0) {
              tasksToComplete = [activeTasks[0]];
              // Clear all but the first one
              activeTasks.slice(1).forEach((t: any) => {
                t.isProcessingDialogue = false;
              });
            }
          }
        }
        
        if (tasksToComplete.length > 0) {
          tasksToComplete.forEach((task) => {
            const dialogueState = task.dialogueState || 
                                 (effect.taskData?.taskState === 'subtask_completed' ? 'subtask_completed' :
                                  effect.taskData?.taskState === 'defined_task_completed' ? 'defined_task_completed' : 'task_completed');
            task.dialogueState = dialogueState;
            task.isProcessingDialogue = false;
            task.isSubmitted = true;
            
            if (effect.taskData?.slots) {
              if (effect.taskData.slots.amount && !task.amount) {
                task.amount = effect.taskData.slots.amount;
              }
              if (effect.taskData.slots.location && !task.location) {
                task.location = effect.taskData.slots.location;
              }
              if (effect.taskData.slots.variant && !task.selectedVariantId) {
                task.selectedVariantId = effect.taskData.slots.variant;
              }
            }
          });
          
          // Trigger callback for single task completion or location completion
          const allTasksAreDefinedCompleted = tasksToComplete.length > 0 && tasksToComplete.every((t: any) => 
            t.dialogueState === 'defined_task_completed'
          );
          const isLocationCompletion = !!effect.wellDefinedTaskId && allTasksAreDefinedCompleted;
          
          if (this.onTaskCompleted && ((!effect.wellDefinedTaskId && tasksToComplete.length === 1) || isLocationCompletion)) {
            const completedTask = tasksToComplete[0];
            const isTerminalState = completedTask.dialogueState === 'defined_task_completed' || 
                                   completedTask.dialogueState === 'task_completed' ||
                                   completedTask.dialogueState === 'subtask_completed' ||
                                   completedTask.dialogueState === 'defined_task_aborted' ||
                                   completedTask.dialogueState === 'task_aborted' ||
                                   completedTask.dialogueState === 'subtask_aborted';
            
            // Call synchronously so follow-up defined-task steps run before the next user message
            // before any user message (e.g. "Utrecht") can be sent — otherwise classify would get wrong params.
            if (isTerminalState || isLocationCompletion) {
              if (this.onTaskCompleted) {
                try {
                  this.onTaskCompleted();
                } catch (error) {
                  console.error('❌ [MARK_TASK_COMPLETED] Error executing callback:', error);
                }
              }
            }
          }
        }
        this.triggerConversationUpdate();
        break;

      case 'MARK_TASK_ABORTED':
        const abortedTask = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
        if (abortedTask) {
          abortedTask.dialogueState = 'task_aborted';
          abortedTask.isProcessingDialogue = false;
        }
        this.triggerConversationUpdate();
        break;

      case 'UPDATE_TASK_OBJECT':
        // Update the active task object with new information (variant, amount, location, etc.)
        let tasksToUpdate: any[] = [];
        if (effect.wellDefinedTaskId) {
          tasksToUpdate = this.conversation.tasks?.filter((t: any) => t.wellDefinedTaskId === effect.wellDefinedTaskId) || [];
        } else if (effect.taskId) {
          let specificTask = this.conversation.tasks?.find((t: any) => t.id === effect.taskId);
          if (!specificTask) {
            specificTask = this.conversation.tasks?.find((t: any) => 
              t.taskId === effect.taskId && t.wellDefinedTaskId
            );
          }
          if (specificTask) {
            tasksToUpdate = [specificTask];
            
            // CRITICAL: If taskId is being updated (child task created from parent), remove parent tasks
            if (effect.updates.taskId !== undefined && effect.updates.taskId !== specificTask.taskId) {
              this.removeParentTasks(
                specificTask.taskId,
                effect.updates.taskId,
                specificTask.wellDefinedTaskId,
                specificTask.id,
                'UPDATE_TASK_OBJECT (effect.taskId path)'
              );
            }
            
            // Mark all other well-defined tasks as NOT processing
            this.conversation.tasks?.forEach((t: any) => {
              if (t.wellDefinedTaskId && t.id !== specificTask.id) {
                t.isProcessingDialogue = false;
              }
            });
          }
        } else if (effect.updates.taskId !== undefined && effect.updates.isProcessingDialogue === true) {
          // Find the currently processing task (it has the old taskId, but we're updating it)
          const activeTask = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
          if (activeTask) {
            const oldTaskId = activeTask.taskId;
            const wellDefinedTaskId = activeTask.wellDefinedTaskId;
            
            console.log('🔄 [TASK_UPDATE] Updating taskId', {
              oldTaskId,
              newTaskId: effect.updates.taskId,
              taskId: activeTask.id
            });
            
            tasksToUpdate = [activeTask];
            
            // CRITICAL: Remove parent tasks when child task is created
            if (oldTaskId !== effect.updates.taskId && wellDefinedTaskId) {
              this.removeParentTasks(
                oldTaskId,
                effect.updates.taskId,
                wellDefinedTaskId,
                activeTask.id,
                'UPDATE_TASK_OBJECT (isProcessingDialogue path)'
              );
            }
            
            // Mark all other tasks as NOT processing
            this.conversation.tasks?.forEach((t: any) => {
              if (t.wellDefinedTaskId && t.id !== activeTask.id) {
                t.isProcessingDialogue = false;
              }
            });
          } else {
            // Fallback: try to find by taskId
            const taskToUpdate = this.conversation.tasks?.find((t: any) => 
              t.taskId === effect.updates.taskId && t.wellDefinedTaskId
            );
            if (taskToUpdate) {
              tasksToUpdate = [taskToUpdate];
              this.conversation.tasks?.forEach((t: any) => {
                if (t.wellDefinedTaskId && t.id !== taskToUpdate.id) {
                  t.isProcessingDialogue = false;
                }
              });
            }
          }
        } else {
          // Fallback: find active task (old behavior)
          const activeTask = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
          if (activeTask) {
            tasksToUpdate = [activeTask];
            
            // CRITICAL: If taskId is being updated (child task created from parent), remove parent tasks
            if (effect.updates.taskId !== undefined && effect.updates.taskId !== activeTask.taskId) {
              this.removeParentTasks(
                activeTask.taskId,
                effect.updates.taskId,
                activeTask.wellDefinedTaskId,
                activeTask.id,
                'UPDATE_TASK_OBJECT (fallback path)'
              );
            }
            
            // Store taskId for next MARK_TASK_COMPLETED if setting isProcessingDialogue to false
            if (effect.updates.isProcessingDialogue === false && activeTask.taskId) {
              this.pendingTaskIdForCompletion = activeTask.taskId;
            }
          }
        }
        
        if (tasksToUpdate.length > 0) {
          tasksToUpdate.forEach((taskToUpdate) => {
            // CRITICAL: When taskId is being updated (child task created from parent), remove parent tasks
            if (effect.updates.taskId !== undefined && effect.updates.taskId !== taskToUpdate.taskId) {
              this.removeParentTasks(
                taskToUpdate.taskId,
                effect.updates.taskId,
                taskToUpdate.wellDefinedTaskId,
                taskToUpdate.id,
                'UPDATE_TASK_OBJECT (forEach path)'
              );
            }
            
            // Preserve existing unit if updates don't have one
            if (!effect.updates.unit && (taskToUpdate.unit || taskToUpdate.billableUnit)) {
              const preservedUnit = taskToUpdate.unit || taskToUpdate.billableUnit;
              effect.updates.unit = preservedUnit;
              effect.updates.billableUnit = preservedUnit;
            }
            
            // Special handling for processedSubtasks - merge with existing ones
            if (effect.updates.processedSubtasks && taskToUpdate.processedSubtasks) {
              const existingMap = new Map();
              taskToUpdate.processedSubtasks.forEach((entry: any) => {
                const key = entry.subtaskId;
                if (key) {
                  existingMap.set(key, entry);
                }
              });
              
              effect.updates.processedSubtasks.forEach((newEntry: any) => {
                const key = newEntry.subtaskId;
                if (key) {
                  if (existingMap.has(key)) {
                    const existing = existingMap.get(key);
                    existingMap.set(key, { ...existing, ...newEntry, unit: newEntry.unit || existing.unit });
                  } else {
                    existingMap.set(key, newEntry);
                  }
                }
              });
              
              effect.updates.processedSubtasks = Array.from(existingMap.values());
            }
            
            // CRITICAL: If we have amount and unit, ensure billableAmount is set
            if (effect.updates.amount !== undefined && effect.updates.unit) {
              effect.updates.billableAmount = {
                amount: effect.updates.amount,
                unit: effect.updates.unit
              };
            } else if (effect.updates.amount !== undefined && taskToUpdate.unit) {
              effect.updates.billableAmount = {
                amount: effect.updates.amount,
                unit: taskToUpdate.unit
              };
              effect.updates.unit = taskToUpdate.unit;
              effect.updates.billableUnit = taskToUpdate.unit;
            }
            
            // Handle name accumulation
            if (effect.updates.name) {
              const newNamePart = effect.updates.name;
              if (taskToUpdate.name) {
                effect.updates.name = `${taskToUpdate.name}; ${newNamePart}`;
              } else {
                effect.updates.name = newNamePart;
              }
            }
            
            // Handle finalName: if set to undefined, copy current name to finalName
            if ('finalName' in effect.updates && effect.updates.finalName === undefined) {
              const currentName = taskToUpdate.name || taskToUpdate.userDescription || '';
              effect.updates.finalName = currentName;
            }
            
            // Apply all updates to the task object
            Object.assign(taskToUpdate, effect.updates);
            
            // Special handling for selectedTaskId
            if (effect.updates.selectedTaskId && taskToUpdate.top_k_tasks) {
              const selectedTask = taskToUpdate.top_k_tasks.find(
                (t: any) => t.task_id === effect.updates.selectedTaskId
              );
              if (selectedTask) {
                taskToUpdate.selectedTask = selectedTask;
              }
            }
          });
        }
        this.triggerConversationUpdate();
        break;

      default:
        break;
    }
  }
}
